import { useState } from 'react';

import { Button } from '../../../shared/Button';
import { PortalPollResults } from '../../components/PortalPollResults';
import { isTocaIcon, TocaIcon } from '../../model/components/TocaIcon';
import { usePortalPostEditor } from '../../Post';
import { ColorInput } from './ColorInput';
import { PositionGridInput } from './PositionGridInput';
import { TocaIconInput } from './TocaIconInput';
import {
    hexToUnityColor,
    isPollSize,
    PollSize,
    ThisOrThatPollData,
    UnityColor,
} from '@lifeserver/shared';

import { getBestContrast } from '../../../utils/colorUtils';

import styles from './PollThisOrThatInput.module.scss';

export const THISORTHAT_ALLOWED_BACKGROUND_COLORS: UnityColor[] = (
    [
        '#000000',
        '#DDE4F0',
        '#FFF594',
        '#FBBD4A',
        '#FF7040',
        '#FF5B5B',
        '#FF8CBC',
        '#7D7DF9',
        '#4DA8F5',
        '#2AC87E',
    ] as const
).map(hexToUnityColor);

const THISORTHAT_TEXT_COLORS: UnityColor[] = (
    ['#000000', '#FFFFFF'] as const
).map(hexToUnityColor);

export const getThisOrThatTextColor = (backgroundColor: UnityColor) => {
    return getBestContrast(backgroundColor, THISORTHAT_TEXT_COLORS);
};

export interface PollThisOrThatProps {
    value: ThisOrThatPollData;
    onChange?: (value: ThisOrThatPollData) => void;
}

export const PollThisOrThatInput = ({
    value,
    onChange,
}: PollThisOrThatProps) => {
    const { isEditingExistingPost, initialPostState } = usePortalPostEditor();
    const [isOpen, setIsOpen] = useState(false);
    const [isClosing, setIsClosing] = useState(false);

    // Read-only mode when initial post state is 'ready'
    const isReadOnlyMode =
        isEditingExistingPost && initialPostState === 'ready';

    const handleToggle = () => {
        if (isOpen) {
            setIsClosing(true);
            setTimeout(() => {
                setIsClosing(false);
                setIsOpen(false);
            }, 300);
        } else {
            setIsOpen(true);
        }
    };
    const onPositionChanged = (position: { x: number; y: number }) => {
        onChange?.({
            ...value,
            position,
        });
    };

    const onSizeChange = (size: 'Medium' | 'Large') => {
        onChange?.({
            ...value,
            size,
            position: { x: 0, y: 0 },
        });
    };

    const onIconChange = (index: number, icon: TocaIcon) => {
        const newAlternatives = [...value.alternatives];
        newAlternatives[index] = {
            ...newAlternatives[index],
            iconName: icon,
        };
        onChange?.({
            ...value,
            alternatives: newAlternatives,
        });
    };

    const onBackgroundColorChange = (
        index: number,
        backgroundColor: UnityColor,
    ) => {
        const newAlternatives = [...value.alternatives];
        newAlternatives[index] = {
            ...newAlternatives[index],
            backgroundColor: backgroundColor,
            textColor: getThisOrThatTextColor(backgroundColor),
        };
        onChange?.({
            ...value,
            alternatives: newAlternatives,
        });
    };

    return (
        <div className={styles.pollContainer}>
            {isReadOnlyMode && (
                <fieldset>
                    <legend>Results</legend>
                    <PortalPollResults
                        pollId={value.id}
                        pollType="ThisOrThat"
                        alternatives={value.alternatives}
                    />
                </fieldset>
            )}

            {isReadOnlyMode ? (
                <fieldset>
                    <legend>Settings</legend>
                    <div className={styles.accordionWrapper}>
                        <Button
                            type="button"
                            onClick={handleToggle}
                            style={{
                                width: '100%',
                                textAlign: 'left',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom:
                                    isOpen || isClosing ? '16px' : '0',
                                backgroundColor: '#E8F4FD',
                                borderColor: '#74B9FF',
                                color: '#2D3436',
                                fontWeight: '500',
                            }}
                        >
                            🔧 View Poll (read only)
                            <span>{isOpen && !isClosing ? '▲' : '▼'}</span>
                        </Button>

                        {(isOpen || isClosing) && (
                            <div
                                className={
                                    isClosing
                                        ? styles.accordionContentClosing
                                        : styles.accordionContent
                                }
                            >
                                <div className={styles.readOnlyOverlay}>
                                    <div className={styles.readOnlyBanner}>
                                        This poll is ready for publication and
                                        can't be changed
                                    </div>
                                    <PollConfigurationFields
                                        value={value}
                                        onPositionChanged={onPositionChanged}
                                        onSizeChange={onSizeChange}
                                        onIconChange={onIconChange}
                                        onBackgroundColorChange={
                                            onBackgroundColorChange
                                        }
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </fieldset>
            ) : (
                <PollConfigurationFields
                    value={value}
                    onPositionChanged={onPositionChanged}
                    onSizeChange={onSizeChange}
                    onIconChange={onIconChange}
                    onBackgroundColorChange={onBackgroundColorChange}
                />
            )}
        </div>
    );
};

interface PollConfigurationFieldsProps {
    value: ThisOrThatPollData;
    onPositionChanged: (position: { x: number; y: number }) => void;
    onSizeChange: (size: PollSize) => void;
    onIconChange: (index: number, icon: TocaIcon) => void;
    onBackgroundColorChange: (index: number, color: UnityColor) => void;
}

const PollConfigurationFields = ({
    value,
    onPositionChanged,
    onSizeChange,
    onIconChange,
    onBackgroundColorChange,
}: PollConfigurationFieldsProps) => {
    const postSize = usePortalPostEditor().post.size;
    return (
        <>
            <PositionGridInput
                value={value.position}
                onChange={onPositionChanged}
                disabled={
                    (postSize === 'Medium' && value.size === 'Medium') ||
                    (postSize === 'Large' && value.size === 'Large')
                        ? [
                              { x: -1.0, y: -1.0 },
                              { x: -1.0, y: 0 },
                              { x: -1.0, y: 1.0 },
                              { x: 1.0, y: -1.0 },
                              { x: 1.0, y: 0 },
                              { x: 1.0, y: 1.0 },
                          ]
                        : []
                }
                bounds={1.0}
            />

            <fieldset>
                <legend>Size</legend>
                <select
                    value={value.size}
                    onChange={(e) =>
                        isPollSize(e.target.value) &&
                        onSizeChange(e.target.value)
                    }
                >
                    <option value="Medium">Medium</option>
                    {postSize === 'Large' && (
                        <option value="Large">Large</option>
                    )}
                </select>
            </fieldset>

            <fieldset>
                <legend>Alternatives</legend>
                <div className={styles.alternativesContainer}>
                    <fieldset>
                        <legend>Left</legend>
                        <fieldset>
                            <legend>Icon</legend>
                            <div className={styles.iconContainer}>
                                <TocaIconInput
                                    selectedIcon={
                                        isTocaIcon(
                                            value.alternatives[0]?.iconName,
                                        )
                                            ? value.alternatives[0]?.iconName
                                            : ''
                                    }
                                    onIconChange={(icon) =>
                                        onIconChange(0, icon)
                                    }
                                />
                            </div>
                        </fieldset>
                        <fieldset>
                            <legend>Background Color</legend>
                            <div className={styles.backgroundColorContainer}>
                                <ColorInput
                                    value={
                                        value.alternatives[0]?.backgroundColor
                                    }
                                    onChange={(color) =>
                                        onBackgroundColorChange(0, color)
                                    }
                                    allowedColors={
                                        THISORTHAT_ALLOWED_BACKGROUND_COLORS
                                    }
                                    layout="grid"
                                    gridColumns={2}
                                />
                            </div>
                        </fieldset>
                    </fieldset>
                    <fieldset>
                        <legend>Right</legend>
                        <fieldset>
                            <legend>Icon</legend>
                            <div className={styles.iconContainer}>
                                <TocaIconInput
                                    selectedIcon={
                                        isTocaIcon(
                                            value.alternatives[1]?.iconName,
                                        )
                                            ? value.alternatives[1]?.iconName
                                            : ''
                                    }
                                    onIconChange={(icon) =>
                                        onIconChange(1, icon)
                                    }
                                />
                            </div>
                        </fieldset>
                        <fieldset>
                            <legend>Background Color</legend>
                            <div className={styles.backgroundColorContainer}>
                                <ColorInput
                                    value={
                                        value.alternatives[1]?.backgroundColor
                                    }
                                    onChange={(color) =>
                                        onBackgroundColorChange(1, color)
                                    }
                                    allowedColors={
                                        THISORTHAT_ALLOWED_BACKGROUND_COLORS
                                    }
                                    layout="grid"
                                    gridColumns={2}
                                />
                            </div>
                        </fieldset>
                    </fieldset>
                </div>
            </fieldset>
        </>
    );
};
